import tokens from '@lansforsakringar/core-tokens'
import { css } from 'lit'

import { vars, scoped, declare, override } from '#utils/styles'

export const locals = scoped('button', [
	'width',
	'min_height',
	'padding_inline',
	'padding_block',
	'border_width',
	'border_color',
	'border_color_pressed',
	'border_color_hover',
	'font_size',
	'color',
	'background',
	'background_pressed',
	'background_hover',
	'color_hover'
] as const)

export default css`
	:host {
		${declare(locals, {
			padding_inline: tokens.spacing_lg,
			padding_block: tokens.spacing_sm,
			border_width: tokens.border_width_emphasis
		})};

		/*
      Small button variant is smaller in height than the minimum allowed clickable area. Thats why we adding vertical margin to make the host element 48px.
		  Margin-block variable is used to be able to subtract the margin from the
			min-height. Otherwise the height of the small button would be 48 + 8 (size-control-sm + vertical margins). */
		--margin-block: 0px;

		width: ${vars(locals.width)};
		display: inline-block;
		vertical-align: middle;
	}

	.button {
		margin-block: var(--margin-block, 0);
		display: inline-flex;
		flex-wrap: nowrap;
		align-items: center;
		justify-content: center;
		gap: ${vars(tokens.spacing_md)};
		width: 100%;
		min-height: calc(${vars(locals.min_height)} - (var(--margin-block) * 2));
		padding-inline: ${vars(locals.padding_inline)};
		padding-block: ${vars(locals.padding_block)};
		border: ${vars(locals.border_width)} solid;
		border-color: ${vars(locals.border_color)};
		overflow: hidden;
		appearance: none;
		user-select: none;
		cursor: pointer;
		font-family: ${vars(tokens.font_family_body)};
		font-size: ${vars(locals.font_size)};
		font-weight: bold;
		line-height: 1.5;
		text-align: left;
		text-wrap: balance;
		text-decoration: none;
		color: ${vars(locals.color)};
		-webkit-font-smoothing: antialiased;
		transition: 100ms var(--ease-in);
		transition-property: background-color, border-color, color, box-shadow;
		border-radius: ${vars(tokens.border_radius_xs)};
		background-color: ${vars(locals.background)};

		&:is(:active, :active:hover) {
			background-color: ${vars(locals.background_pressed)};
			border-color: ${vars(locals.border_color_pressed)};
		}

		&:focus {
			outline: 0;
		}

		&.focus-visible:focus-visible {
			outline: var(--focus-outline);
			outline-offset: var(--focus-outline-offset);
		}

		&:is(:focus-visible, :hover) {
			background-color: ${vars(locals.background_hover)};
			border-color: ${vars(locals.border_color_hover)};
			color: ${vars(locals.color_hover)};
			transition-timing-function: var(--ease-out);
		}

		.main {
			text-box: cap alphabetic;
		}

		&.primary {
			${override(
				locals.background,
				locals.background,
				tokens.color_background_button_primary
			)};
			${override(
				locals.background_pressed,
				locals.background_pressed,
				`color-mix(in srgb,${vars(tokens.color_state_layer_on_dark)} calc(${vars(tokens.effect_opacity_state_pressed)} * 100%), ${vars(tokens.color_background_button_primary)})`
			)};
			${override(
				locals.background_hover,
				locals.background_hover,
				`color-mix(in srgb,${vars(tokens.color_state_layer_on_dark)} calc(${vars(tokens.effect_opacity_state_hover)} * 100%), ${vars(tokens.color_background_button_primary)})`
			)};
			${override(locals.color, locals.color, tokens.color_text_inverted)};
			${override(
				locals.color_hover,
				locals.color_hover,
				tokens.color_text_inverted
			)};
			${override(
				locals.border_color,
				locals.border_color,
				tokens.color_border_on_button_primary
			)};
			${override(
				locals.border_color_pressed,
				locals.border_color_pressed,
				`color-mix(in srgb,${vars(tokens.color_state_layer_on_dark)} calc(${vars(tokens.effect_opacity_state_pressed)} * 100%), ${vars(tokens.color_background_button_primary)})`
			)};
			${override(
				locals.border_color_hover,
				locals.border_color_hover,
				`color-mix(in srgb,${vars(tokens.color_state_layer_on_dark)} calc(${vars(tokens.effect_opacity_state_hover)} * 100%), ${vars(tokens.color_background_button_primary)})`
			)};
		}

		&.secondary {
			${override(
				locals.background,
				locals.background,
				tokens.color_background_button_secondary
			)};
			${override(
				locals.background_pressed,
				locals.background_pressed,
				`color-mix(in srgb, ${vars(tokens.color_state_layer_on_light)} calc(${vars(tokens.effect_opacity_state_pressed)} * 100%), ${vars(tokens.color_background_button_secondary)})`
			)};
			${override(
				locals.background_hover,
				locals.background_hover,
				`color-mix(in srgb, ${vars(tokens.color_state_layer_on_light)} calc(${vars(tokens.effect_opacity_state_hover)} * 100%), ${vars(tokens.color_background_button_secondary)})`
			)};
			${override(
				locals.color,
				locals.color,
				tokens.color_text_on_button_secondary
			)};
			${override(
				locals.color_hover,
				locals.color_hover,
				tokens.color_text_on_button_secondary
			)};
			${override(
				locals.border_color,
				locals.border_color,
				tokens.color_border_on_button_secondary
			)};
			${override(
				locals.border_color_pressed,
				locals.border_color_pressed,
				tokens.color_border_on_button_secondary
			)};
			${override(
				locals.border_color_hover,
				locals.border_color_hover,
				tokens.color_border_on_button_secondary
			)};
		}

		&.small {
			--margin-block: ${vars(tokens.spacing_xs)};

			${override(locals.min_height, locals.min_height, tokens.size_control_sm)};
			${override(locals.font_size, locals.font_size, '0.875rem')};
		}

		&.large {
			${override(
				locals.min_height,
				locals.min_height,
				tokens.size_control_default
			)};
			${override(locals.font_size, locals.font_size, '1.125rem')};
			${override(
				locals.padding_block,
				locals.padding_block,
				tokens.spacing_md
			)};
			${override(
				locals.padding_inline,
				locals.padding_inline,
				tokens.spacing_lg
			)};
		}
	}
`
